import { Schema, StringValue } from '@coveo/bueno';
import { configuration } from '../../app/common-reducers.js';
import { buildSearchParameterSerializer } from '../../features/search-parameters/search-parameter-serializer.js';
import { deepEqualAnyOrder } from '../../utils/compare-utils.js';
import { loadReducerError } from '../../utils/errors.js';
import { validateInitialState } from '../../utils/validate-payload.js';
import { buildController, } from '../controller/headless-controller.js';
import { buildSearchParameterManager } from '../search-parameter-manager/headless-search-parameter-manager.js';
export const initialStateSchema = new Schema({
    fragment: new StringValue(),
});
/**
 * Creates a `UrlManager` controller instance.
 *
 * @param engine - The headless engine.
 * @param props - The configurable `UrlManager` properties.
 * @returns A `UrlManager` controller instance.
 *
 * @group Controllers
 * @category UrlManager
 */
export function buildUrlManager(engine, props) {
    let lastRequestId;
    function updateLastRequestId() {
        lastRequestId = engine.state.search.requestId;
    }
    function hasRequestIdChanged() {
        return lastRequestId !== engine.state.search.requestId;
    }
    if (!loadUrlManagerReducers(engine)) {
        throw loadReducerError;
    }
    validateInitialState(engine, initialStateSchema, props.initialState, 'buildUrlManager');
    const controller = buildController(engine);
    let previousFragment = props.initialState.fragment;
    updateLastRequestId();
    const searchParameterManager = buildSearchParameterManager(engine, {
        initialState: {
            parameters: deserializeFragment(previousFragment),
        },
    });
    return {
        ...controller,
        subscribe(listener) {
            const strictListener = () => {
                const newFragment = this.state.fragment;
                if (!areFragmentsEquivalent(previousFragment, newFragment) &&
                    hasRequestIdChanged()) {
                    previousFragment = newFragment;
                    listener();
                }
                updateLastRequestId();
            };
            strictListener();
            return engine.subscribe(strictListener);
        },
        get state() {
            return {
                fragment: buildSearchParameterSerializer().serialize(searchParameterManager.state.parameters),
            };
        },
        synchronize(fragment) {
            previousFragment = fragment;
            const parameters = deserializeFragment(fragment);
            searchParameterManager.synchronize(parameters);
        },
    };
}
function areFragmentsEquivalent(fragment1, fragment2) {
    if (fragment1 === fragment2) {
        return true;
    }
    const params1 = deserializeFragment(fragment1);
    const params2 = deserializeFragment(fragment2);
    return deepEqualAnyOrder(params1, params2);
}
function deserializeFragment(fragment) {
    return buildSearchParameterSerializer().deserialize(fragment);
}
function loadUrlManagerReducers(engine) {
    engine.addReducers({ configuration });
    return true;
}
